import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Memo } from './types';

// 1 事業あたりのメモ上限。AI チャットスレッドの page_size=100 に揃える（モック仕様）。
export const MEMO_MAX_COUNT_PER_PROJECT = 100;

type CreateMemoInput = {
  projectId: string;
  body: string;
  threadId?: string;
};

type UpdateMemoInput = {
  id: string;
  body: string;
};

// メモはモック実装で BE 連携なし。localStorage に永続化してリロード/再訪問でも保持する。
type ProjectMemoState = {
  memosByProject: Record<string, Memo[]>;
  /** AI チャットの「このスレッドを引用してメモを作成」ボタン → メモページ間の受け渡し用（永続化しない） */
  pendingThreadId: string | undefined;

  /** 上限を超えていた場合は undefined を返す（呼び出し側でエラーハンドリング） */
  addMemo: (input: CreateMemoInput) => Memo | undefined;
  updateMemo: (input: UpdateMemoInput) => Memo | undefined;
  deleteMemo: (id: string) => void;
  setPendingThreadId: (threadId: string | undefined) => void;
};

// BE 実装時削除予定。mock 用
export const useProjectMemoStore = create<ProjectMemoState>()(
  persist(
    (set, get) => ({
      memosByProject: {},
      pendingThreadId: undefined,

      addMemo: ({ projectId, body, threadId }) => {
        const currentList = get().memosByProject[projectId] ?? [];
        if (currentList.length >= MEMO_MAX_COUNT_PER_PROJECT) {
          return undefined;
        }
        const now = new Date().toISOString();
        const newMemo: Memo = {
          id: crypto.randomUUID(),
          projectId,
          body,
          threadId,
          createdAt: now,
          updatedAt: now,
        };
        set((state) => ({
          memosByProject: {
            ...state.memosByProject,
            [projectId]: [newMemo, ...(state.memosByProject[projectId] ?? [])],
          },
        }));
        return newMemo;
      },

      updateMemo: ({ id, body }) => {
        const now = new Date().toISOString();
        const memosByProject = get().memosByProject;
        // メモは projectId に紐付くため、所属プロジェクトを特定して、その配列だけ更新する。
        // 他プロジェクトの配列参照は変えないことでリレンダリングも最小化する。
        for (const [pid, list] of Object.entries(memosByProject)) {
          const target = list.find((memo) => memo.id === id);
          if (!target) continue;
          const updated: Memo = { ...target, body, updatedAt: now };
          const nextList = list.map((memo) => (memo.id === id ? updated : memo));
          set({ memosByProject: { ...memosByProject, [pid]: nextList } });
          return updated;
        }
        return undefined;
      },

      deleteMemo: (id) => {
        const memosByProject = get().memosByProject;
        for (const [pid, list] of Object.entries(memosByProject)) {
          const index = list.findIndex((memo) => memo.id === id);
          if (index === -1) continue;
          const nextList = list.filter((memo) => memo.id !== id);
          set({ memosByProject: { ...memosByProject, [pid]: nextList } });
          return;
        }
      },

      setPendingThreadId: (threadId) => {
        set({ pendingThreadId: threadId });
      },
    }),
    {
      name: 'project-memo-store',
      version: 1,
      // pendingThreadId はチャット→メモページ間の一時的な受け渡し用なので永続化しない
      partialize: (state) => ({ memosByProject: state.memosByProject }),
    },
  ),
);
